import moment from "moment";
import {
  QuoteSummary,
  QuoteSummaryRes,
  VendorQuoteInfo,
} from "../../../../services/interfaces/IVendorQuoteAssessment";
import {
  formatNumberwithCommas,
  numberFormatter,
} from "../../../../utils/formatter";
import DangerArrowUpSm from "../../../../assets/up-arrow-sm-danger.svg";
import WarnArrowUpSm from "../../../../assets/up-arrow-sm-warn.svg";
import SafeArrowDownSm from "../../../../assets/down-arrow-sm-safe.svg";
import DownloadIcon from "../../../../assets/download-green.svg";
import SortDefault from "../../../../assets/sort_default.svg";
import SortAsc from "../../../../assets/sort_asc.svg";
import SortDsc from "../../../../assets/sort_desc.svg";
import {
  Table,
  Column as RSuiteColumn,
  ColumnGroup as RsuiteColumnGroup,
  HeaderCell,
  Cell,
} from "rsuite-table";
import { useState, useEffect } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { appInsights } from "../../../../router/router";
import CustomModal from "../../../../components/custom-modal";
import { Link } from "react-router-dom";
import { Button, Tooltip, Whisper } from "rsuite";
import { convertDateToMonthYear } from "../../../../utils/helpers/mapModulesToIcon";
import ProductInfoIcon from "../../../../assets/info-circle-grey.svg";

type Props = {
  data: QuoteSummaryRes;
  month: string;
  district: Array<string>;
  vendor: Array<string>;
  commodity: string | null;
  isMonthOf: boolean;
  indexUploaded: boolean;
};

const getTableDimensions = (width: number) => {
  if (width > 1600 && width < 1800) {
    return { height: 450, rowHeight: 32 };
  } else if (width === 1600) {
    return { height: 420, rowHeight: 28 };
  } else if (width === 1422) {
    return { height: 400, rowHeight: 25 };
  } else if (width >= 1024 && width <= 1164) {
    return { height: 300, rowHeight: 20 };
  } else if (width < 900) {
    return { height: 250, rowHeight: 15 };
  } else if (width > 1800 && width < 2500) {
    return { height: 550, rowHeight: 34 };
  } else if (width > 2500 && width < 3500) {
    return { height: 700, rowHeight: 45 };
  } else if (width >= 3500) {
    return { height: 900, rowHeight: 90 };
  }
  return { height: 350, rowHeight: 28 }; // Default dimensions
};

const getTableColWidth = (vendor: Array<string>) => {
  switch (true) {
    case vendor?.length === 1:
      return 150;
    case vendor?.length > 4 && window.innerWidth >= 1600:
      return 220;
    case vendor?.length > 4 && window.innerWidth < 1024:
      return 120;
    case vendor?.length > 4 && window.innerWidth < 1600:
      return 170;
    case window.innerHeight >= 900 && window.innerHeight < 1200:
      return 300;
    case window.innerHeight > 1200:
      return 330;
    case window.innerWidth >= 1600 && window.innerWidth <= 1920:
      return 220;
    case window.innerWidth > 1920 && window.innerWidth <= 2560:
      return 280;
    case window.innerWidth >= 2580:
      return 320;
    case window.innerWidth < 1600:
      return 120;
    default:
      return 180;
  }
};

const getTableHeaderHeight = (width: number) => {
  if (window.innerHeight >= 900) {
    return 180;
  }
  if (width >= 1920 && width < 3500) {
    return 180;
  } else if (width >= 3500) {
    return 400;
  }
  return 100;
};

const QuoteDetails = ({
  data: quoteDetails,
  month,
  district,
  vendor,
  commodity,
  isMonthOf,
  indexUploaded,
}: Props) => {
  // const apiRes: any = quoteDetails;
  const vendorList = Object.keys(quoteDetails.vendorSummary);
  const targetMonth =
    commodity === "Copper Wire"
      ? isMonthOf
        ? convertDateToMonthYear(month)
        : moment(month, "YYYY-MM-DD").format("DD MMMM YYYY")
      : moment.utc(month).format("MMMM");
  const copperWireDateLabel = isMonthOf ? "Month Of" : "Week Of";
  const [tableColumnWidth, setTableColumnWidth] = useState(150);
  const [tableDimensions, setTableDimensions] = useState({
    height: 310,
    rowHeight: 28,
  });
  const [tableHeaderHeight, setTableHeaderHeight] = useState(100);
  const [sortColumn, setSortColumn] = useState("");
  const [sortType, setSortType] = useState();
  const [tableData, setTableData] = useState<any>([]);
  useEffect(() => {
    setTableData(quoteDetails.data);
  }, [quoteDetails.data]);
  useEffect(() => {
    const handleResize = () => {
      const dimensions = getTableDimensions(window.innerWidth);
      const tableHeadHeight = getTableHeaderHeight(window.innerWidth);
      const tablecolwidth = getTableColWidth(vendorList);
      setTableHeaderHeight(tableHeadHeight);
      setTableDimensions(dimensions);
      setTableColumnWidth(tablecolwidth);
    };

    // Call once on mount to set initial dimensions
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize); // Clean up
    };
  }, []);

  const ProjectedReleaseHeader = () => {
    return (
      <div className="flex flex-col justify-start items-start">
        <span>
          Projected Releases (
          {quoteDetails.commodity === "Copper Wire" ? "lbs" : "TLs"})
        </span>
        <span className="text-deloitteGreen">{quoteDetails.commodity}</span>
      </div>
    );
  };

  const TargetCostHeader = () => {
    return (
      <div className="flex flex-col justify-start items-start">
        <span>Target Cost ($/cft)</span>
        <span className="text-deloitteGreen">{quoteDetails.size_trade}</span>
      </div>
    );
  };

  const GainForecastHeader = () => {
    return (
      <div className="flex justify-center">
        Projected Releases & Target Cost for{" "}
        {commodity === "Copper Wire" && copperWireDateLabel}&nbsp;
        <span className="text-deloitteGreen">{targetMonth}</span>
      </div>
    );
  };

  const BlendedCostHeader = () => {
    return (
      <div className="flex justify-center">
        <span className="text-deloitteGreen">
          {quoteDetails.size_trade}&nbsp;
        </span>
        Quotes vs. Target Cost for {commodity === "Copper Wire" && copperWireDateLabel}
        &nbsp;
        <span className="text-deloitteGreen">{targetMonth}</span>
      </div>
    );
  };

  const VendorHeader = (vendor: string) => {
    const obj = quoteDetails.vendorSummary[vendor];
    const date = new Date(obj.quotation_start_date);
    return (
      <div className="flex flex-col items-start justify-start">
        {commodity === "Copper Wire" ? (
          <>
            {" "}
            <span className="font-bold">{vendor}</span>
            <span className="italic flex gap-2 items-center font-bold text-[#A3A3A3] text-[1.4vh]">
              {obj.contract_validity}
              <Whisper
                speaker={<Tooltip placement="auto">{obj.duration}</Tooltip>}
              >
                <></>
                {/* <img
                  src={ProductInfoIcon}
                  alt="info"
                  className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                ></img> */}
              </Whisper>
            </span>
          </>
        ) : (
          <>
            {" "}
            <span className="font-bold">{vendor}</span>
            <span className="italic font-bold text-[#A3A3A3] text-[1.4vh]">
              {obj.quotation_no === 10
                ? "Mid Month"
                : `Quote #${obj.quotation_no}`}{" "}
              valid from {moment.utc(date).format("ll")}
            </span>
          </>
        )}
      </div>
    );
  };

  const VendorBody = (rowdata: QuoteSummary, vendor: string) => {
    const data: VendorQuoteInfo = rowdata[vendor];
    return (
      <div className="flex justify-between w-full">
        {typeof data?.latest_quote === "number" ? (
          commodity?.toLowerCase() === "copper wire" ? (
            <span>${formatNumberwithCommas(data.latest_quote, true)}</span>
          ) : (
            <span>{numberFormatter(data.latest_quote, 2, "$")}</span>
          )
        ) : (
          "-"
        )}
        {typeof data?.latest_quote === "number" ? (
          <span
            className={`flex flex-nowrap gap-1 ${data.percent_difference <= 0
              ? "text-deloitteGreen"
              : data.percent_difference > 0.04
                ? "text-alert"
                : "text-warning"
              }`}
          >
            {numberFormatter(Math.abs(data.percent_difference * 100), 1) + "%"}
            {data.percent_difference <= 0 && (
              <img
                // width={"80%"}
                className={"w-[0.8vw]"}
                src={SafeArrowDownSm}
                alt="safe"
              ></img>
            )}

            {data.percent_difference > 0 && data.percent_difference <= 0.04 && (
              <img
                // width={"80%"}
                className={"w-[0.8vw]"}
                src={WarnArrowUpSm}
                alt="warn"
              ></img>
            )}

            {data.percent_difference > 0.04 && (
              <img
                // width={"80%"}
                className={"w-[0.8vw]"}
                src={DangerArrowUpSm}
                alt="danger"
              ></img>
            )}
          </span>
        ) : (
          <span>-</span>
        )}
      </div>
    );
  };

  const handleQuoteDetailsDownloadExcel = async () => {
    // Robust Excel column letter math (supports A..Z, AA..AZ, BA.. etc.)
    const colToIndex = (col: string) => {
      let n = 0;
      const s = col.toUpperCase();
      for (let i = 0; i < s.length; i++) {
        n = n * 26 + (s.charCodeAt(i) - 64);
      }
      return n;
    };

    const indexToCol = (n: number) => {
      let s = "";
      while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
      }
      return s;
    };

    const getNextcolumn = (previousColumn: string, length: number) =>
      indexToCol(colToIndex(previousColumn) + length);

    const VendorHeaderString = (vendor: string) => {
      const obj = quoteDetails.vendorSummary[vendor];
      const date = new Date(obj.quotation_start_date);
      // return `Quote #${obj.quotation_no} requested on ${moment
      //   .utc(date)
      //   .format("ll")}`;
      if (obj.quotation_no && obj.quotation_start_date) {
        return `${obj.quotation_no === 10 ? "Mid Month" : `Quote #${obj.quotation_no}`
          } valid from ${moment.utc(date).format("ll")}`;
      } else {
        return "";
      }
    };

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Quote Detail Summary");

    worksheet.mergeCells("A1:A2");
    worksheet.mergeCells("B1:B2");
    worksheet.mergeCells("C1:C2");
    worksheet.mergeCells("D1:D2");
    worksheet.mergeCells("E1:E2");

    worksheet.getCell("A1").value = "Commodity";
    worksheet.getCell("B1").value = "District";
    worksheet.getCell("C1").value =
      `Projected Releases (TLs) ${quoteDetails.commodity}`;
    worksheet.getCell("D1").value = "Target Cost ($/cft) '3/4 EMT";
    worksheet.getCell("E1").value = "Monthly Average TLs in past 3 months";

    const lastColumn = getNextcolumn("F", vendorList.length - 1);
    worksheet.mergeCells(`F1:${lastColumn}1`);
    worksheet.getCell("F1").value = "Supplier Market Share in District";

    let currentColumn = "F";
    vendorList.forEach((vendor, i) => {
      currentColumn = getNextcolumn("F", i);
      worksheet.getCell(`${currentColumn}2`).value =
        vendor !== "null" ? vendor : "-";
    });

    vendorList.forEach((vendor) => {
      const startValue = getNextcolumn(currentColumn, 1);
      const lastValue = getNextcolumn(currentColumn, 2);
      worksheet.mergeCells(`${startValue}1:${lastValue}1`);
      worksheet.getCell(`${startValue}1`).value = `${vendor !== "null" ? vendor : "-"
        } ${VendorHeaderString(vendor)}`;
      worksheet.getCell(`${startValue}2`).value = "Quote($/cft)'3/4 EMT";
      worksheet.getCell(`${lastValue}2`).value = "Variation from Target";
      currentColumn = lastValue;
    });

    worksheet.columns = [
      { key: "commodity" },
      { key: "district" },
      { key: "projected_releases", width: 50 },
      { key: "target_cost", width: 50, style: { numFmt: "$#,##0.00" } },
      { key: "monthly_average", width: 50 },
      ...vendorList.map((vendor, i) => {
        return { key: `Vendor Market ${vendor}`, style: { numFmt: "0.00%" } };
      }),
      ...vendorList
        .map((vendor, i) => {
          return [
            { key: `${vendor} Quote` },
            { key: `${vendor} Variation`, style: { numFmt: "0.00%" } },
          ];
        })
        .flat(),
    ];

    quoteDetails.data.forEach((row) => {
      let newRow: any = {
        commodity:
          quoteDetails.commodity !== null ? quoteDetails.commodity : "-",
        district: row?.district !== null ? row?.district : "-",
        projected_releases: row?.forecast !== null ? row?.forecast : "-",
        target_cost: row?.target_cost !== null ? row?.target_cost : "-",
        monthly_average:
          quoteDetails.downloadData?.[row?.district]?.forecastPreviousMonths ??
          "-",
      };
      vendorList.forEach((vendor) => {
        const districtDownloadData =
          quoteDetails.downloadData?.[`${row?.district}`];
        newRow[`Vendor Market ${vendor}`] = districtDownloadData?.[`${vendor}`]
          ? parseFloat(
            districtDownloadData[`${vendor}`].replace("%", ""),
          ) / 100
          : "-";

        newRow[`${vendor} Quote`] = row[vendor]?.latest_quote
          ? row[vendor]?.latest_quote
          : "-";
        newRow[`${vendor} Variation`] = row[vendor]?.percent_difference
          ? (row[vendor]?.percent_difference * 100) / 100
          : "-";
      });
      worksheet.addRow(newRow);
    });

    // Apply styling to all data rows
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      row.eachCell({ includeEmpty: true }, (cell, cellNumber) => {
        cell.alignment = {
          wrapText:
            rowNumber !== 2 ||
              (rowNumber === 2 && cellNumber >= 3 && cellNumber <= 5)
              ? true
              : false,
          vertical: "middle",
          horizontal: rowNumber <= 2 ? "center" : "left",
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        if (rowNumber <= 2) {
          cell.font = { bold: true };
          row.height = 30;
        }
      });
    });

    worksheet.columns.forEach((column: any, i: number) => {
      let maxLength = 0;
      if (column._key.includes("Quote")) {
        column.numFmt = "$#,##0.00";
      }
      column.eachCell({ includeEmpty: true }, (cell: any, j: number) => {
        if (i < 2 || (i >= 5 && j > 1)) {
          if (cell.value) {
            const cellValue = cell.value.toString();
            maxLength = Math.max(maxLength, cellValue.length);
          }
        } else if (i >= 2 && i < 5) {
          maxLength = 20;
        }
      });
      column.width = maxLength + 2; // Add some padding to the width
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `Quote Detail Summary_${quoteDetails.commodity}.xlsx`,
    );
    appInsights.trackEvent({
      name: "Vendor Quote Assessment: Quote Details",
      properties: {
        description: `${localStorage.getItem(
          "userEmail",
        )} has downloaded "Quote Detail Summary_${quoteDetails.commodity
          }.xlsx" Report`,
      },
    });
  };

  const handleQuoteDetailsDownloadExcelForWire = async () => {
    // Robust Excel column letter math (supports A..Z, AA..AZ, BA.. etc.)
    const colToIndex = (col: string) => {
      let n = 0;
      const s = col.toUpperCase();
      for (let i = 0; i < s.length; i++) {
        n = n * 26 + (s.charCodeAt(i) - 64);
      }
      return n;
    };

    const indexToCol = (n: number) => {
      let s = "";
      while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
      }
      return s;
    };

    const getNextcolumn = (previousColumn: string, length: number) =>
      indexToCol(colToIndex(previousColumn) + length);

    const VendorHeaderString = (v: string) => {
      const obj = quoteDetails.vendorSummary[v];
      if (!obj) return "";
      return `${obj.contract_validity}`;
    };

    const vendors = Array.from(
      new Set(
        (vendor && vendor.length
          ? vendor
          : Object.keys(quoteDetails.vendorSummary)
        ).filter((v) => v && v !== "null"),
      ),
    );

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Quote Detail Summary");

    worksheet.mergeCells("A1:A2");
    worksheet.mergeCells("B1:B2");
    worksheet.mergeCells("C1:C2");
    worksheet.mergeCells("D1:D2");
    worksheet.mergeCells("E1:E2");

    worksheet.getCell("A1").value = "Commodity";
    worksheet.getCell("B1").value = "District";
    worksheet.getCell("C1").value =
      `Projected Releases (lbs) ${quoteDetails.commodity}`;
    worksheet.getCell("D1").value =
      `Target Cost ($/cft) ${quoteDetails.size_trade}`;
    worksheet.getCell("E1").value = "Monthly Average lbs in past 3 months";

    // Start vendor quote/variation columns directly after column E
    let currentCol = "E";
    vendors.forEach((v) => {
      const startCol = getNextcolumn(currentCol, 1);
      const endCol = getNextcolumn(currentCol, 2);

      worksheet.mergeCells(`${startCol}1:${endCol}1`);
      worksheet.getCell(`${startCol}1`).value =
        `${v || "-"} ${VendorHeaderString(v)}`;
      worksheet.getCell(`${startCol}2`).value =
        `Quote($/cft) ${quoteDetails.size_trade}`;
      worksheet.getCell(`${endCol}2`).value = "Variation from Target";

      currentCol = endCol;
    });

    worksheet.columns = [
      { key: "commodity" },
      { key: "district" },
      { key: "projected_releases", width: 40 },
      { key: "target_cost", width: 26, style: { numFmt: "$#,##0.00" } },
      { key: "monthly_average", width: 40 },
      ...vendors.flatMap((v) => [
        { key: `${v} Quote`, style: { numFmt: "$#,##0.00" } },
        { key: `${v} Variation`, style: { numFmt: "0.00%" } },
      ]),
    ];

    quoteDetails.data.forEach((row: any) => {
      const newRow: any = {
        commodity: quoteDetails.commodity ?? "-",
        district: row?.district ?? "-",
        projected_releases: row?.forecast ?? "-",
        target_cost: row?.target_cost ?? "-",
        monthly_average:
          quoteDetails.downloadData?.[row?.district]?.forecastPreviousMonths ??
          "-",
      };

      vendors.forEach((v) => {
        newRow[`${v} Quote`] = row?.[v]?.latest_quote ?? "-";

        const diff = row?.[v]?.percent_difference;
        newRow[`${v} Variation`] = typeof diff === "number" ? diff : "-";
      });

      worksheet.addRow(newRow);
    });

    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: rowNumber <= 2 ? "center" : "left",
          wrapText: rowNumber > 2,
        };
        if (rowNumber <= 2) {
          cell.font = { bold: true };
          row.height = 28;
        }
      });
    });

    worksheet.columns.forEach((column: any) => {
      let maxLength = column.width ?? 12;
      column.eachCell({ includeEmpty: true }, (cell: any) => {
        const value = cell.value;
        if (value && typeof value === "string") {
          maxLength = Math.max(maxLength, value.length);
        }
        if (typeof value === "number") {
          maxLength = Math.max(maxLength, value.toString().length);
        }
      });
      column.width = Math.min(Math.max(maxLength + 2, 10), 60);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `Quote Detail Summary_${quoteDetails.commodity}.xlsx`,
    );

    appInsights.trackEvent({
      name: "Vendor Quote Assessment: Quote Details",
      properties: {
        description: `${localStorage.getItem("userEmail")} has downloaded "Quote Detail Summary_${quoteDetails.commodity}.xlsx" Report`,
      },
    });
  };

  const sortByColumnName = (
    data: any[],
    order: "asc" | "desc" | null,
    column: string,
  ) => {
    if (!order) return data;
    return [...data].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (columnName: string) => {
    let nextSortType: any;
    if (sortColumn !== columnName) {
      nextSortType = "asc";
      setSortColumn(columnName);
      setSortType(nextSortType);
      setTableData(sortByColumnName(quoteDetails.data, "asc", columnName));
    } else if (sortType === "asc") {
      nextSortType = "desc";
      setSortType(nextSortType);
      setTableData(sortByColumnName(quoteDetails.data, "desc", columnName));
    } else if (sortType === "desc") {
      nextSortType = "";
      setSortColumn("");
      setSortType(nextSortType);
      setTableData(quoteDetails.data); // original order
    }
  };

  return (
    <>
      <div className="w-full flex justify-between items-center  bg-white font-bold p-pd13 shadow-module-card border border-[#bebebe] rounded-md flex-nowrap">
        <div className="flex">
          <span className="text-fs16 mr-[1vh]">Quote Details Summary</span>
          <div
            onClick={() => {
              if (commodity === "Copper Wire") {
                handleQuoteDetailsDownloadExcelForWire();
              } else {
                handleQuoteDetailsDownloadExcel();
              }
            }}
            className="cursor-pointer flex items-center"
          >
            <img src={DownloadIcon} className="w-[1vw] h-[1vw]" />
          </div>
        </div>
        {/* Legend */}
        <div className="flex self-end justify-end items-center flex-nowrap gap-4">
          <div className="flex gap-1 items-center">
            <span className="bg-deloitteGreen w-[2.5vh] h-[2.5vh]"></span>
            <span className="font-semibold text-[1.5vh]">
              At or Below Target Cost
            </span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="bg-amber w-[2.5vh] h-[2.5vh]"></span>
            <span className="font-semibold text-[1.5vh]">
              0-4% Above Target Cost
            </span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="bg-alert w-[2.5vh] h-[2.5vh]"></span>
            <span className="font-semibold text-[1.5vh]">
              4% Above Target Cost
            </span>
          </div>
        </div>
      </div>

      {/* Table */}

      <div className="w-full quote-details-table h-[60vh]">
        <Table
          headerHeight={tableHeaderHeight}
          // data={quoteDetails.data}
          data={tableData}
          bordered
          autoHeight={
            district?.length <= 6 && district?.length !== 0 ? true : false
          } // Enable autoHeight if only one row
          fillHeight={
            district?.length >= 6 || district?.length === 0 ? true : false
          }
          rowHeight={tableDimensions?.rowHeight}
          cellBordered
          hover={false}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={handleSort}
        >
          <RsuiteColumnGroup
            verticalAlign="middle"
            align="center"
            fixed
            header={<GainForecastHeader />}
          >
            <RSuiteColumn
              sortable
              fixed
              minWidth={tableColumnWidth - 40}
              flexGrow={vendor?.length === 1 ? 0.5 : 1}
              align="left"
            // verticalAlign="middle"
            >
              <HeaderCell
                renderSortIcon={(sortType) => {
                  const iconSize = 12;
                  if (sortColumn === "district" && sortType === "asc") {
                    return (
                      <img width={iconSize} height={iconSize} src={SortAsc} />
                    );
                  } else if (sortColumn === "district" && sortType === "desc") {
                    return (
                      <img width={iconSize} height={iconSize} src={SortDsc} />
                    );
                  } else
                    return (
                      <img
                        width={iconSize}
                        height={iconSize}
                        src={SortDefault}
                      />
                    );
                }}
              >
                District
              </HeaderCell>
              <Cell dataKey="district" />
            </RSuiteColumn>
            <RSuiteColumn
              fixed
              minWidth={tableColumnWidth + 20}
              flexGrow={vendor?.length === 1 ? 0.5 : 1}
              align="left"
              verticalAlign="middle"
              sortable
            >
              <HeaderCell
                renderSortIcon={(sortType) => {
                  const iconSize = 12;
                  if (sortColumn === "forecast" && sortType === "asc") {
                    return (
                      <img width={iconSize} height={iconSize} src={SortAsc} />
                    );
                  } else if (sortColumn === "forecast" && sortType === "desc") {
                    return (
                      <img width={iconSize} height={iconSize} src={SortDsc} />
                    );
                  } else
                    return (
                      <img
                        width={iconSize}
                        height={iconSize}
                        src={SortDefault}
                      />
                    );
                }}
              >
                <ProjectedReleaseHeader />
              </HeaderCell>
              <Cell dataKey="forecast">
                {(rowData) =>
                  `${rowData?.forecast ? (commodity?.toLowerCase() === "copper wire" ? `${formatNumberwithCommas(rowData.forecast)}` : rowData?.forecast) : "-"}`
                }
              </Cell>
            </RSuiteColumn>
            <RSuiteColumn
              fixed
              minWidth={tableColumnWidth - 30}
              flexGrow={vendor?.length === 1 ? 0.5 : 1}
              align="left"
              verticalAlign="middle"
            >
              <HeaderCell>
                <TargetCostHeader />
              </HeaderCell>
              <Cell dataKey="target_cost">
                {(rowData) =>
                  `${rowData.target_cost
                    ? commodity?.toLowerCase() === "copper wire"
                      ? `$${formatNumberwithCommas(rowData.target_cost, true)}`
                      : numberFormatter(rowData.target_cost, 2, "$")
                    : "-"
                  }`
                }
              </Cell>
            </RSuiteColumn>
          </RsuiteColumnGroup>
          <RsuiteColumnGroup
            verticalAlign="middle"
            align="center"
            header={<BlendedCostHeader />}
          >
            {vendorList
              ?.filter(
                (vendor: string | null) =>
                  vendor !== null && vendor !== "null" && vendor !== undefined,
              )
              .map((vendor: string) => {
                return (
                  <RSuiteColumn
                    minWidth={tableColumnWidth}
                    flexGrow={1}
                    align="left"
                    verticalAlign="middle"
                  >
                    <HeaderCell>{VendorHeader(vendor)}</HeaderCell>
                    <Cell dataKey={vendor}>
                      {(rowdata: any) => VendorBody(rowdata, vendor)}
                    </Cell>
                  </RSuiteColumn>
                );
              })}
          </RsuiteColumnGroup>
        </Table>
      </div>
      {!indexUploaded && (
        <CustomModal
          openModal={true}
          closeModal={() => null}
          isloading={false}
          header="Warning !!!"
          footer=""
          closeable={false}
        >
          <div className="flex justify-start items-center w-full px-[1vh] pb-[2.5vh]">
            <span className="text-[0.9vw] font-semibold text-red-800">
              <span>Commodity Index is not updated for </span>
              <span className="text-deloitteGreen">
                {convertDateToMonthYear(month, -1)}
              </span>
              .<span> Please update the index first for </span>
              <span className="text-deloitteGreen">
                {convertDateToMonthYear(month, -1)}
              </span>
              <span> to get the latest target cost.</span>
            </span>
          </div>
          <div className="flex justify-center items-center">
            <Link
              to={{ pathname: "/upload-quote" }}
              state={{ hiddenParam: "additionalInputs" }}
              className="w-[50%] flex justify-center items-center no-underline hover:no-underline"
            >
              <Button className="w-full font-bold text-[0.8vw] pl-[0.2vw] bg-deloitteGreen text-white hover:bg-deloitteGreen hover:text-white active:bg-deloitteGreen focus:bg-deloitteGreen">
                Update Index Now
              </Button>
            </Link>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default QuoteDetails;
